/**
 * @copyright Copyright 2021-2023 Epic Systems Corporation
 * @file One row representing one app in the Nominated Apps widget
 * @module Epic.AppOrchard.Dashboard.NominatedAppRow
 */

import {
	Box,
	Button,
	Image,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	VStack,
	Wrap,
	WrapItem,
} from "@chakra-ui/react";
import { IDispatch } from "@epic/react-redux-booster";
import { AOLink, DownloadBuildIcon, ImplementationGuideLink } from "ao/components/Core";
import { EnvironmentType } from "ao/data";
import { INominatedApp, IUserSecurity } from "ao/types";
import React, { FC, memo, useCallback, useState } from "react";
import { VscFeedback } from "react-icons/vsc";
import { NominatedAppsButtons, NominationInfo, NotImplementingNowInfo } from ".";
import { WidgetAppBadge, WidgetAppRow } from "../../../widgetHelpers";

function getOtherOrgsMessage(totalOrgs: number, numOrgNames: number): string | null {
	if (totalOrgs > numOrgNames) {
		const numOtherOrgs = totalOrgs - numOrgNames;
		return `and ${numOtherOrgs} other organization${numOtherOrgs !== 1 ? "s" : ""}`;
	}
	return null;
}

interface INominatedAppRowProps {
	app: INominatedApp;
	userSecurity: IUserSecurity;
	marketplaceUrl: string;
	downloadUrl: string;
	tesseractLogo: string;
	orgId: number;
	participantTermsId: number | undefined; // undefined while loading
	dispatch: IDispatch;
	installingLogo: string;
	liveLogo: string;
	isGalleryEnabled: boolean | undefined;
}

/** One row representing one app in the Nominated Apps widget  */
export const NominatedAppRow: FC<INominatedAppRowProps> = memo((props: INominatedAppRowProps) => {
	const {
		app,
		userSecurity,
		marketplaceUrl,
		downloadUrl,
		tesseractLogo,
		orgId,
		participantTermsId,
		dispatch,
		installingLogo,
		liveLogo,
		isGalleryEnabled,
	} = props;

	//#region state/handlers
	// used to focus the next focusable element after the contact button after contacting since the contact button will be disabled
	const [appLinkRef, setAppLinkRef] = useState<React.RefObject<HTMLAnchorElement> | null>(null);
	const [showingOrgsModal, setShowingOrgsModal] = useState<boolean>(false);
	const [orgsModalEnvType, setOrgsModalEnvType] = useState<EnvironmentType>(EnvironmentType.NonProd);

	const orgsModalTitle =
		orgsModalEnvType === EnvironmentType.NonProd ? "Installing Organizations" : "Live Organizations";

	const orgNameList =
		orgsModalEnvType === EnvironmentType.NonProd ? app.sharedInstallingOrgNames : app.sharedLiveOrgNames;
	const totalOrgs = orgsModalEnvType === EnvironmentType.NonProd ? app.numInstallingOrgs : app.numLiveOrgs;
	const otherOrgsMessage = getOtherOrgsMessage(totalOrgs, orgNameList.length);

	const hasInstallingOrgNames = app.sharedInstallingOrgNames.length > 0;
	const hasLiveOrgNames = app.sharedLiveOrgNames.length > 0;

	const handleShowOrgsModal = useCallback(
		(envType: EnvironmentType) => () => {
			setOrgsModalEnvType(envType);
			setShowingOrgsModal(true);
		},
		[],
	);
	const handleToggleOrgsModal = useCallback(
		(show: boolean) => () => {
			setShowingOrgsModal(show);
		},
		[],
	);

	const handleSetAppLinkRef = useCallback(
		(linkRef: React.RefObject<HTMLAnchorElement> | null) => setAppLinkRef(linkRef),
		[],
	);

	const installingOrgsHelptext = `${app.numInstallingOrgs} organization${
		app.numInstallingOrgs === 1 ? " is" : "s are"
	} installing this app.${hasInstallingOrgNames ? " Click for a list." : ""}`;

	const liveOrgsHelptext = `${app.numLiveOrgs} organization${
		app.numLiveOrgs === 1 ? " is" : "s are"
	} live on this app.${hasLiveOrgNames ? " Click for a list." : ""}`;

	const viewReviewsUrl = `${marketplaceUrl}${app.appId}&tab=reviews`;
	const reviewsHelptext = `${app.numReviews} total reviews for this app. Click to see all reviews.`;

	const downloadPageUrl = (app.isAvailableInGallery ? marketplaceUrl : downloadUrl) + app.appId;

	//#endregion
	return (
		<>
			<WidgetAppRow
				appId={app.appId}
				appName={app.appName}
				subtitle={<Box title="The organization that created this app">{app.appOrgName}</Box>}
				linkUrl={downloadPageUrl}
				onLinkRefCreated={handleSetAppLinkRef}
				linkTitle="Open this app's listing"
				belowLogoContent={
					<>
						{app.tesseractEnabled && (
							<Image
								src={tesseractLogo}
								h="18px"
								w="18px"
								title="This app is enabled for Tesseract."
								aria-label="This app is enabled for Tesseract"
								alt="Tesseract logo"
								display="inline-block"
							/>
						)}
						{app.hasDownloadableBuild && <DownloadBuildIcon />}
					</>
				}
				additionalAppInfo={
					<VStack spacing="0.3em" width="max-content">
						<NominationInfo
							w="100%"
							latestAppVersionId={app.appId}
							nominations={app.nominations}
							userSecurity={userSecurity}
						></NominationInfo>
						{app.notNowDecision && (
							<NotImplementingNowInfo
								latestAppVersionId={app.appId}
								notNowDecision={app.notNowDecision}
								userSecurity={userSecurity}
							></NotImplementingNowInfo>
						)}
					</VStack>
				}
				bottomContent={
					app.implementationGuideUrl && <ImplementationGuideLink url={app.implementationGuideUrl} />
				}
				actions={
					userSecurity.canPurchase &&
					participantTermsId && (
						<NominatedAppsButtons
							app={app}
							orgId={orgId}
							dispatch={dispatch}
							appLinkRef={appLinkRef}
							downloadPageUrl={downloadPageUrl}
							isGalleryEnabled={isGalleryEnabled}
						/>
					)
				}
				middleColumnContent={
					<Wrap spacing="0.25em">
						{isGalleryEnabled && (
							<>
								<WrapItem>
									<WidgetAppBadge
										asButton={hasInstallingOrgNames}
										buttonProps={{
											onClick: hasInstallingOrgNames
												? handleShowOrgsModal(EnvironmentType.NonProd)
												: undefined,
										}}
										title={installingOrgsHelptext}
										value={app.numInstallingOrgs.toString()}
										useHoverTooltip
									>
										<Image src={installingLogo} h="20px" w="20px" display="inline" />
									</WidgetAppBadge>
								</WrapItem>
								<WrapItem>
									<WidgetAppBadge
										asButton={hasLiveOrgNames}
										buttonProps={{
											onClick: hasLiveOrgNames
												? handleShowOrgsModal(EnvironmentType.Prod)
												: undefined,
										}}
										title={liveOrgsHelptext}
										value={app.numLiveOrgs.toString()}
										useHoverTooltip
									>
										<Image src={liveLogo} h="20px" w="20px" display="inline" />
									</WidgetAppBadge>
								</WrapItem>
							</>
						)}

						{
							//disable for now
							false && app.numReviews > 0 && isGalleryEnabled && (
								<WrapItem>
									<AOLink url={viewReviewsUrl} target="_blank" title={reviewsHelptext}>
										<WidgetAppBadge
											title={reviewsHelptext}
											value={app.numReviews.toString()}
										>
											<VscFeedback
												size={20}
												color="darkblue"
												style={{ display: "inline" }}
											/>
										</WidgetAppBadge>
									</AOLink>
								</WrapItem>
							)
						}
					</Wrap>
				}
			></WidgetAppRow>
			<Modal isOpen={showingOrgsModal} onClose={handleToggleOrgsModal(false)}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>{orgsModalTitle}</ModalHeader>
					<ModalCloseButton />
					<ModalBody pt={0}>
						{orgNameList.map((orgName, index) => (
							<Box key={index} fontWeight="bold" ml="0.5em">
								{orgName}
							</Box>
						))}
						{otherOrgsMessage && <Box ml="0.5em">{otherOrgsMessage}</Box>}
					</ModalBody>
					<ModalFooter>
						<Button colorScheme="blue" onClick={handleToggleOrgsModal(false)}>
							Ok
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
});
